import React, { useContext, useEffect, useMemo, useState } from "react";
import {
	Modal,
	Box,
	Typography,
	Grid,
	Paper,
	Accordion,
	AccordionSummary,
	AccordionDetails,
	List,
	ListItem,
	ListItemIcon,
	ListItemText,
	Checkbox,
	Button,
	CircularProgress,
	IconButton,
	Tooltip,
	Dialog,
	DialogContent,
} from "@mui/material";
import ErrorIcon from "@mui/icons-material/Error";

import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import CloseIcon from "@mui/icons-material/Close";
import { useTranslation } from "react-i18next";
import { GlobalContext } from "../../GlobalContext";
import { obtenerDatos, agregarDato } from "../../api/config";

const USE_MOCK = false;
const ACC_SUMMARY_H = 60;
const ERROR_KEY_PATTERN = /^(error|errors|failure|failures|failed|failed_contracts)$/i;
const FAILURE_STATUSES = new Set(["error", "failed", "failure"]);

const MOCK_CONTRACTS_PAYLOAD = {
	Staff: {
		Diurno: ["BRA Escala 6x1 Min 5.30 a 6.00"],
		Nocturno: [],
		Madrugada: ["BRA Escala 5x2 Min 8.00", "BRA Escala 4x3 Min 7.00"],
	},
	UKG: {
		Diurno: [
			{ id: 101, kronos_name: "FT 42 2342 Diurno" },
			{ id: 102, kronos_name: "FT 42 2342 Diurno (2)" },
			{ id: 103, kronos_name: "FT 42 2342 Diurno (3)" },
			{ id: 104, kronos_name: "FT 42 2342 Diurno (4)" },
			{ id: 105, kronos_name: "FT 42 2342 Diurno (5)" },
		],
		Nocturno: [{ id: 201, kronos_name: "PT 30 1122 Nocturno" }],
		Madrugada: [],
	},
};

const formatErrorValue = (value) => {
	if (value == null) return "";
	if (typeof value === "string" || typeof value === "number") return String(value);
	if (Array.isArray(value)) return value.map(formatErrorValue).filter(Boolean).join("\n");
	if (typeof value !== "object") return "";

	const contractName = value.kronos_name ?? value.contract_name ?? value.nombre;
	const detail = value.message ?? value.detail ?? value.reason ?? value.msg;

	if (detail != null) {
		const message = formatErrorValue(detail);
		return contractName && message ? `${contractName}: ${message}` : message;
	}

	return Object.entries(value)
		.map(([key, item]) => {
			const message = formatErrorValue(item);
			return message ? `${key}: ${message}` : "";
		})
		.filter(Boolean)
		.join("\n");
};

const extractContractErrors = (payload) => {
	const errors = [];

	const visit = (value) => {
		if (!value || typeof value !== "object") return;

		if (Array.isArray(value)) {
			value.forEach(visit);
			return;
		}

		const status = typeof value.status === "string" ? value.status.toLowerCase() : "";
		const isFailure = FAILURE_STATUSES.has(status) || value.success === false || value.ok === false;
		let hasExplicitError = false;

		Object.entries(value).forEach(([key, item]) => {
			if (ERROR_KEY_PATTERN.test(key)) {
				hasExplicitError = true;
				if (item == null || item === false || item === 0) return;
				const message = formatErrorValue(item);
				if (message) errors.push(message);
			} else if (item && typeof item === "object") {
				visit(item);
			}
		});

		if (isFailure && !hasExplicitError) {
			const message = formatErrorValue(value);
			if (message) errors.push(message);
		}
	};

	visit(payload);
	return [...new Set(errors)];
};

const extractRequestErrors = (error) => {
	const payload = error?.response?.data;
	if (payload == null) return error?.message ? [error.message] : [];
	if (typeof payload !== "object") return [String(payload)];

	const messages = extractContractErrors(payload);
	if (messages.length) return messages;

	const detail = payload.detail ?? payload.message ?? payload.reason;
	const message = formatErrorValue(detail);
	return message ? [message] : [];
};

/** Tooltip icon */
const InfoIconButton = ({ title, placement = "left" }) => (
	<Tooltip arrow placement={placement} title={title}>
		<IconButton
			size="small"
			sx={{
				backgroundColor: "rgba(255,255,255,0.12)",
				width: 26,
				height: 26,
				"&:hover": { backgroundColor: "rgba(255,255,255,0.18)" },
			}}
		>
			<InfoOutlinedIcon fontSize="small" sx={{ color: "common.white" }} />
		</IconButton>
	</Tooltip>
);

/** Normalización */
const normalizeStaff = (staffRaw = {}) => {
	if (!staffRaw || typeof staffRaw !== "object") return [];
	return Object.entries(staffRaw).map(([shift, list]) => ({
		shift,
		items: (Array.isArray(list) ? list : []).filter(Boolean).map((name) => ({
			id: `staff:${shift}:${String(name)}`,
			label: String(name),
		})),
	}));
};

const ukgItemId = (shift, c) => {
	if (c?.id) return `ukg:${shift}:${c.id}`;
	const name = c?.kronos_name ?? "unknown";
	const tipo = c?.id_tipo_contrato ?? "nt";
	const horas = c?.hora_contrato ?? "nh";
	const z = c?.zcero ?? "z";
	const ci = c?.cotinf ?? "ci";
	const cs = c?.cotsup ?? "cs";
	return `ukg:${shift}:${name}:${tipo}:${horas}:${z}:${ci}:${cs}`;
};

const normalizeUkg = (ukgRaw = {}) => {
	if (!ukgRaw || typeof ukgRaw !== "object") return [];
	return Object.entries(ukgRaw).map(([shift, list]) => ({
		shift,
		items: (Array.isArray(list) ? list : []).filter(Boolean).map((c) => ({
			id: ukgItemId(shift, c),
			label: c?.kronos_name || "Contrato sin nombre",
			raw: c,
		})),
	}));
};

/** Layout */
const modalSx = { display: "flex", alignItems: "center", justifyContent: "center" };

const modalBoxSx = {
	width: "80%",
	maxWidth: 1100,
	height: "80vh",
	bgcolor: "background.paper",
	border: "1px solid",
	borderColor: "divider",
	borderRadius: "20px",
	p: 3,
	overflow: "hidden",
	display: "flex",
	flexDirection: "column",
};

const headerRowSx = {
	display: "flex",
	alignItems: "center",
	justifyContent: "space-between",
	gap: 2,
	flexShrink: 0,
};

const contentSx = { mt: 2, flex: 1, minHeight: 0, overflow: "hidden" };

const panelPaperSx = {
	borderRadius: "16px",
	overflow: "hidden",
	height: "100%",
	display: "flex",
	flexDirection: "column",
};

const checkboxHeaderWhiteSx = {
	p: 0,
	color: "common.white",
	"&.Mui-checked": { color: "common.white" },
	"&.MuiCheckbox-indeterminate": { color: "common.white" },
};

const panelHeaderSx = {
	bgcolor: "grey.900",
	color: "common.white",
	px: 2.5,
	py: 2,
	height: 56,
	display: "flex",
	alignItems: "center",
	justifyContent: "space-between",
	gap: 2,
	flexShrink: 0,
};

const checkboxDarkSx = {
	p: 0,
	color: "text.primary",
	"&.Mui-checked": { color: "text.primary" },
	"&.MuiCheckbox-indeterminate": { color: "text.primary" },
};

const ACC_LINE_COLOR = "grey.300";

const accordionGroupSx = {
	border: "1px solid",
	borderColor: ACC_LINE_COLOR,
	borderTopLeftRadius: 0,
	borderTopRightRadius: 0,
	borderBottomLeftRadius: "16px",
	borderBottomRightRadius: "16px",
	overflow: "hidden",
	bgcolor: "background.paper",
	width: "100%",
	minHeight: 0,
	display: "flex",
	flexDirection: "column",
};

const accordionItemSx = (idx, total) => ({
	m: 0,
	boxShadow: "none",
	bgcolor: "background.paper",
	"&:before": { display: "none" },
	"&.Mui-expanded": { m: 0 },
	borderBottom: idx === total - 1 ? "none" : "1px solid",
	borderColor: ACC_LINE_COLOR,
});

const accSummaryBaseSx = {
	minHeight: ACC_SUMMARY_H,
	"&.Mui-expanded": { minHeight: ACC_SUMMARY_H },
	px: 2.5,
	"& .MuiAccordionSummary-content": {
		my: 0,
		display: "flex",
		alignItems: "center",
		gap: 1.25,
	},
	"& .MuiAccordionSummary-content.Mui-expanded": { my: 0 },
};

// ✅ Columna fija (tabulado) para alinear checkbox de tipo + items (UKG)
const CHECK_COL = 34;
const CHECK_GAP = 10;

const ukgSummarySx = {
	...accSummaryBaseSx,
	pl: 1.5,
	pr: 2.5,
	"& .MuiAccordionSummary-content": {
		my: 0,
		display: "flex",
		alignItems: "center",
		gap: 0,
	},
};

const ukgSummaryCheckColSx = {
	width: CHECK_COL,
	minWidth: CHECK_COL,
	display: "flex",
	justifyContent: "center",
	alignItems: "center",
	mr: `${CHECK_GAP}px`,
};

const ukgItemRowSx = (loading) => ({
	px: 3,
	"&:hover": {
		bgcolor: loading ? "transparent" : "action.hover",
		cursor: loading ? "default" : "pointer",
	},
});

const ukgItemIconSx = {
	minWidth: CHECK_COL,
	width: CHECK_COL,
	mr: `${CHECK_GAP}px`,
	justifyContent: "center",
};

const ModalImport = ({ open, onClose, onImporting, onImported, onImportError }) => {
	const { t } = useTranslation();
	const { site_id } = useContext(GlobalContext);

	const [loadingContracts, setLoadingContracts] = useState(false);
	const [contractsData, setContractsData] = useState(null);
	const [error, setError] = useState("");
	const [selectedUkgIds, setSelectedUkgIds] = useState(() => new Set());
	const [expandedStaff, setExpandedStaff] = useState(false);
	const [expandedUkg, setExpandedUkg] = useState(false);
	const [isImporting, setIsImporting] = useState(false);
	const isBusy = loadingContracts || isImporting;
	const effectiveData = contractsData;

	const staffSections = useMemo(() => normalizeStaff(effectiveData?.Staff), [effectiveData]);
	const ukgSections = useMemo(() => normalizeUkg(effectiveData?.UKG), [effectiveData]);

	const staffOrder = useMemo(() => staffSections.map((s) => s.shift), [staffSections]);

	const ukgSectionsOrdered = useMemo(() => {
		const index = new Map(staffOrder.map((shift, i) => [shift, i]));

		return [...ukgSections].sort((a, b) => {
			const ia = index.has(a.shift) ? index.get(a.shift) : Number.MAX_SAFE_INTEGER;
			const ib = index.has(b.shift) ? index.get(b.shift) : Number.MAX_SAFE_INTEGER;
			return ia - ib;
		});
	}, [ukgSections, staffOrder]);

	const staffDetailsMaxH = `calc(100% - ${ACC_SUMMARY_H * staffSections.length}px)`;
	const ukgDetailsMaxH = `calc(100% - ${ACC_SUMMARY_H * ukgSections.length}px)`;

	const [errorModal, setErrorModal] = useState({ open: false, title: "", message: "" });

	const selectedUkgArray = useMemo(() => {
		const out = [];
		for (const section of ukgSections) {
			for (const item of section.items || []) {
				if (selectedUkgIds.has(item.id)) out.push(item.raw);
			}
		}
		return out;
	}, [ukgSections, selectedUkgIds]);

	const shiftLabel = (shift) => {
		const map = {
			Diurno: t("shifts.diurno", "Diurno"),
			Nocturno: t("shifts.nocturno", "Nocturno"),
			Madrugada: t("shifts.madrugada", "Madrugada"),
		};
		return map[shift] ?? shift;
	};

	const toggleUkgItemById = (id) => {
		if (isBusy) return;
		setSelectedUkgIds((prev) => {
			const next = new Set(prev);
			if (next.has(id)) next.delete(id);
			else next.add(id);
			return next;
		});
	};

	const toggleUkgItem = (item) => toggleUkgItemById(item.id);

	const getAllUkgIds = () => ukgSections.flatMap((s) => (s.items || []).map((it) => it.id));

	const isUkgAllChecked = () => {
		const allIds = getAllUkgIds();
		return allIds.length > 0 && allIds.every((id) => selectedUkgIds.has(id));
	};

	const isUkgAllIndeterminate = () => {
		const allIds = getAllUkgIds();
		const someSelected = allIds.some((id) => selectedUkgIds.has(id));
		const allSelected = allIds.length > 0 && allIds.every((id) => selectedUkgIds.has(id));
		return someSelected && !allSelected;
	};

	const toggleUkgAll = () => {
		if (isBusy) return;
		const allIds = getAllUkgIds();

		setSelectedUkgIds((prev) => {
			const next = new Set(prev);
			const allSelected = allIds.length > 0 && allIds.every((id) => next.has(id));
			if (allSelected) allIds.forEach((id) => next.delete(id));
			else allIds.forEach((id) => next.add(id));
			return next;
		});
	};

	const toggleShiftAll = (shift) => {
		if (isBusy) return;

		const section = ukgSections.find((s) => s.shift === shift);
		const ids = (section?.items || []).map((it) => it.id);

		setSelectedUkgIds((prev) => {
			const next = new Set(prev);
			const allSelected = ids.length > 0 && ids.every((id) => next.has(id));
			if (allSelected) ids.forEach((id) => next.delete(id));
			else ids.forEach((id) => next.add(id));
			return next;
		});
	};

	const isShiftAllChecked = (section) =>
		section.items.length > 0 && section.items.every((it) => selectedUkgIds.has(it.id));

	const isShiftIndeterminate = (section) =>
		section.items.some((it) => selectedUkgIds.has(it.id)) &&
		!section.items.every((it) => selectedUkgIds.has(it.id));

	const fetchContractsFromEndpoint = async (sid) => {
		setLoadingContracts(true);
		setError("");
		setContractsData(null);

		try {
			if (USE_MOCK) {
				await new Promise((r) => setTimeout(r, 600));
				setContractsData(MOCK_CONTRACTS_PAYLOAD);
				setSelectedUkgIds(new Set());
				return MOCK_CONTRACTS_PAYLOAD;
			}

			const payload = await obtenerDatos(`/contracts/get_match/${sid}`);
			const hasUkg =
				payload && typeof payload === "object" && payload.UKG && typeof payload.UKG === "object";
			const hasStaff =
				payload && typeof payload === "object" && payload.Staff && typeof payload.Staff === "object";

			if (!hasUkg && !hasStaff) throw new Error("INVALID_CONTRACTS_PAYLOAD");

			setContractsData(payload);
			setSelectedUkgIds(new Set());
			return payload;
		} catch (err) {
			console.error("[ModalImport][GET] error:", err);
			setError(t("modal_import.error_loading"));
			setContractsData(null);
			throw err;
		} finally {
			setLoadingContracts(false);
		}
	};

	useEffect(() => {
		if (!open) return;
		let isMounted = true;

		const run = async () => {
			setError("");
			try {
				await fetchContractsFromEndpoint(site_id);
			} catch (e) {
				if (isMounted) {
					setError(t("modal_import.error_loading"));
					setContractsData(null);
				}
			}
		};

		run();
		return () => {
			isMounted = false;
		};
	}, [open, site_id, t]);

	const handleConfirm = async () => {
		if (isImporting) return;
		setErrorModal({ open: false, title: "", message: "" });

		// ✅ “importando…” en el padre (snackbar global)
		onImporting?.();
		setIsImporting(true);

		try {
			const response = await agregarDato(
				`/contracts/default_create/${site_id}`,
				selectedUkgArray,
			);
			const contractErrors = extractContractErrors(response);

			if (contractErrors.length) {
				setErrorModal({
					open: true,
					title: t("modal_import.error.title"),
					message: contractErrors.join("\n"),
				});
				onImportError?.(response);
				return;
			}

			onImported?.({ count: selectedUkgArray.length });

			onClose();
		} catch (err) {
			console.error("[ModalImport][POST] error:", err);
			const requestErrors = extractRequestErrors(err);

			setErrorModal({
				open: true,
				title: t("modal_import.error.title"),
				message: requestErrors.join("\n") || t("modal_import.error.message"),
			});

			onImportError?.(err); // ✅ esto cerrará el snackbar en el padre
		} finally {
			setIsImporting(false);
		}
	};

	const hasData = !!effectiveData;
	const showContent = !loadingContracts && !error && hasData;

	return (
		<Modal open={open} onClose={onClose} sx={modalSx}>
			<Box sx={modalBoxSx}>
				{/* Header */}
				<Box sx={headerRowSx}>
					<Box sx={{ display: "flex", alignItems: "center", gap: 1.5, minWidth: 0 }}>
						<Typography variant="h6" sx={{ fontWeight: 700, whiteSpace: "nowrap" }}>
							{t("modal_import.title")}
						</Typography>

						{loadingContracts ? (
							<Box sx={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center" }}>
								<CircularProgress size={22} />
							</Box>
						) : null}
					</Box>

					<IconButton
						onClick={onClose}
						aria-label={t("modal_import.close_aria")}
						sx={{
							borderRadius: "10px",
							width: 36,
							height: 36,
							"&:hover": { backgroundColor: "action.hover" },
						}}
					>
						<CloseIcon />
					</IconButton>
				</Box>

				{/* Estados */}
				{error ? (
					<Typography sx={{ mt: 1, color: "warning.main", flexShrink: 0 }}>{error}</Typography>
				) : null}

				{loadingContracts ? (
					<Box sx={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center" }}>
						<CircularProgress />
					</Box>
				) : null}

				{!loadingContracts && error ? (
					<Box sx={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center", px: 2 }}>
						<Typography sx={{ color: "text.secondary", textAlign: "center" }}>{error}</Typography>
					</Box>
				) : null}

				{!loadingContracts && !error && !hasData ? (
					<Box sx={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center", px: 2 }}>
						<Typography sx={{ color: "text.secondary", textAlign: "center" }}>
							{t("modal_import.no_data")}
						</Typography>
					</Box>
				) : null}

				{/* Contenido */}
				{showContent ? (
					<Box sx={contentSx}>
						<Grid container spacing={2} sx={{ height: "100%" }}>
							{/* STAFF */}
							<Grid item xs={12} md={6} sx={{ height: "100%" }}>
								<Paper elevation={0} sx={panelPaperSx}>
									<Box sx={panelHeaderSx}>
										<Typography sx={{ fontWeight: 700 }}>{t("modal_import.staff.title")}</Typography>
										<InfoIconButton title={t("modal_import.staff.tooltip")} placement="left" />
									</Box>

									<Box sx={{ flex: 1, minHeight: 0, overflow: "hidden" }}>
										<Box sx={accordionGroupSx}>
											{staffSections.map((section, idx) => (
												<Accordion
													key={section.shift}
													expanded={expandedStaff === section.shift}
													onChange={(_, isExpanded) => setExpandedStaff(isExpanded ? section.shift : false)}
													disableGutters
													sx={accordionItemSx(idx, staffSections.length)}
												>
													<AccordionSummary expandIcon={<ExpandMoreIcon />} sx={accSummaryBaseSx}>
														<Typography sx={{ fontWeight: 600 }}>
															{shiftLabel(section.shift)}{" "}
															<Box component="span" sx={{ color: "text.secondary", fontWeight: 400 }}>
																({section.items.length})
															</Box>
														</Typography>
													</AccordionSummary>

													<AccordionDetails sx={{ p: 0 }}>
														<Box sx={{ maxHeight: staffDetailsMaxH, overflowY: "auto", p: 1.5 }}>
															<List dense disablePadding>
																{section.items.length === 0 ? (
																	<ListItem disableGutters sx={{ px: 2.5 }}>
																		<ListItemText primary={t("modal_import.empty")} />
																	</ListItem>
																) : (
																	section.items.map((item) => (
																		<ListItem key={item.id} disableGutters sx={{ px: 2.5 }}>
																			<ListItemText primary={item.label} />
																		</ListItem>
																	))
																)}
															</List>
														</Box>
													</AccordionDetails>
												</Accordion>
											))}
										</Box>
									</Box>
								</Paper>
							</Grid>

							{/* UKG */}
							<Grid item xs={12} md={6} sx={{ height: "100%" }}>
								<Paper elevation={0} sx={panelPaperSx}>
									<Box sx={panelHeaderSx}>
										<Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
											<Checkbox
												disabled={isBusy || getAllUkgIds().length === 0}
												checked={isUkgAllChecked()}
												indeterminate={isUkgAllIndeterminate()}
												sx={checkboxHeaderWhiteSx}
												onClick={(e) => {
													e.stopPropagation();
													toggleUkgAll();
												}}
											/>
											<Typography sx={{ fontWeight: 700 }}>{t("modal_import.ukg.title")}</Typography>
										</Box>

										<InfoIconButton title={t("modal_import.ukg.tooltip")} placement="left" />
									</Box>

									<Box sx={{ flex: 1, minHeight: 0, overflow: "hidden" }}>
										<Box sx={accordionGroupSx}>
											{ukgSectionsOrdered.map((section, idx) => (
												<Accordion
													key={section.shift}
													expanded={expandedUkg === section.shift}
													onChange={(_, isExpanded) => setExpandedUkg(isExpanded ? section.shift : false)}
													disableGutters
													sx={accordionItemSx(idx, ukgSections.length)}
												>
													<AccordionSummary expandIcon={<ExpandMoreIcon />} sx={ukgSummarySx}>
														{/* ✅ Columna fija del checkbox del tipo (TABULADO) */}
														<Box sx={ukgSummaryCheckColSx} onClick={(e) => e.stopPropagation()}>
															<Tooltip title={t("modal_import.ukg.select_shift_tooltip")}>
																<Checkbox
																	size="small"
																	disabled={isBusy || section.items.length === 0}
																	checked={isShiftAllChecked(section)}
																	indeterminate={isShiftIndeterminate(section)}
																	sx={checkboxDarkSx}
																	onClick={(e) => {
																		e.stopPropagation();
																		toggleShiftAll(section.shift);
																	}}
																	onFocus={(e) => e.stopPropagation()}
																/>
															</Tooltip>
														</Box>

														<Typography sx={{ fontWeight: 600 }}>
															{shiftLabel(section.shift)}{" "}
															<Box component="span" sx={{ color: "text.secondary", fontWeight: 400 }}>
																({section.items.length})
															</Box>
														</Typography>
													</AccordionSummary>

													<AccordionDetails sx={{ p: 0 }}>
														<Box sx={{ maxHeight: ukgDetailsMaxH, overflowY: "auto", py: 1 }}>
															<List dense disablePadding>
																{section.items.length === 0 ? (
																	<ListItem disableGutters sx={{ px: 2.5 }}>
																		<ListItemText primary={t("modal_import.empty")} />
																	</ListItem>
																) : (
																	section.items.map((item) => {
																		const checked = selectedUkgIds.has(item.id);

																		return (
																			<ListItem
																				key={item.id}
																				disableGutters
																				sx={ukgItemRowSx(loadingContracts)}
																				onClick={() => toggleUkgItem(item)}
																			>
																				{/* ✅ Misma columna fija para checkbox del item */}
																				<ListItemIcon sx={ukgItemIconSx}>
																					<Checkbox
																						size="small"
																						disabled={isBusy}
																						edge="start"
																						checked={checked}
																						sx={checkboxDarkSx}
																						onClick={(e) => {
																							e.stopPropagation();
																							toggleUkgItem(item);
																						}}
																					/>
																				</ListItemIcon>

																				<ListItemText primary={item.label} />
																			</ListItem>
																		);
																	})
																)}
															</List>
														</Box>
													</AccordionDetails>
												</Accordion>
											))}
										</Box>
									</Box>
								</Paper>
							</Grid>
						</Grid>
					</Box>
				) : null}

				{/* Footer */}
				<Box
					sx={{
						pt: 2,
						flexShrink: 0,
						display: "flex",
						alignItems: "center",
						justifyContent: "space-between",
						mt: 2,
					}}
				>
					<Button
						variant="outlined"
						onClick={onClose}
						disabled={loadingContracts}
						sx={{ textTransform: "none" }}
					>
						{t("modal_import.actions.close")}
					</Button>

					<Button
						disabled={!showContent || isBusy || selectedUkgArray.length === 0}
						variant="contained"
						color="primary"
						onClick={handleConfirm}
						sx={{ textTransform: "none" }}
					>
						{t("modal_import.actions.import_selected", { count: selectedUkgArray.length })}
					</Button>
				</Box>

				<Dialog
					open={errorModal.open}
					onClose={() => setErrorModal({ open: false, title: "", message: "" })}
				>
					<DialogContent sx={{ p: 4 }}>
						<Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 2 }}>
							<ErrorIcon color="error" sx={{ fontSize: 44 }} />
							<Typography variant="h6" sx={{ fontWeight: 800 }}>
								{errorModal.title}
							</Typography>
						</Box>

						<Typography sx={{ mb: 3, whiteSpace: "pre-line" }}>{errorModal.message}</Typography>

						<Box sx={{ display: "flex", justifyContent: "flex-end" }}>
							<Button
								variant="contained"
								color="primary"
								onClick={() => setErrorModal({ open: false, title: "", message: "" })}
								sx={{ textTransform: "none", borderRadius: "999px", px: 4 }}
							>
								{t("modal_import.error.accept")}
							</Button>
						</Box>
					</DialogContent>
				</Dialog>
			</Box>
		</Modal>
	);
};

export default ModalImport;
// {/* Snackbar */}

// 				{/* Error Modal */}
